import { useEffect, useLayoutEffect, useRef } from 'react'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Typography, Divider } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useIsMobile } from '@/hooks/Responsive'
import { useModalFocusManager } from '@/hooks/useModalFocusManager'

export default function BasicModal({
  open,
  onClose,
  title,
  children,
  width = '800px',
  maxWidth = false,
  hideCloseButton = false,
  actions,
  variant = 'bottomsheet', // 'bottomsheet' | 'modal' | 'fullmodal'
}) {
  useModalFocusManager(open)
  const { isMobile } = useIsMobile()
  const dialogRef = useRef(null)

  // 모달이 #root 에 aria-hidden 을 붙이기 전에, 강의보기 버튼 등 뒤에 남은 포커스를 뺀다.
  useLayoutEffect(() => {
    if (!open) return
    const active = document.activeElement
    if (!(active instanceof HTMLElement)) return
    if (dialogRef.current?.contains(active)) return
    active.blur()
  }, [open])

  // 포커스 강제 이동
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        const firstFocusable = dialogRef.current?.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        firstFocusable?.focus()
      }, 100)
    }
  }, [open])

  // variant별 Paper 스타일
  const getPaperStyle = (theme) => {
    // bottomsheet
    if (variant === 'bottomsheet') {
      return {
        position: 'fixed',
        bottom: 0,
        left: 0,
        m: 0,
        width: '100%',
        maxWidth: '100%',
        borderTopLeftRadius: theme.shape.radius('lg'),
        borderTopRightRadius: theme.shape.radius('lg'),
        display: 'flex',
        flexDirection: 'column',
        minHeight: '326px',
        maxHeight: 'calc(100dvh - 60px)',
        overflow: 'hidden',
        p: isMobile ? 2 : 3,
      }
    }

    // fullmodal
    if (variant === 'fullmodal') {
      return {
        position: 'fixed',
        top: 0,
        left: 0,
        m: 0,
        borderRadius: theme.shape.radius('lg'),
        width: '100%',
        height: '100%',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: isMobile ? 2 : 3,
      }
    }

    // 기본 modal
    return {
      maxWidth: width,
      width: '100%',
      borderRadius: theme.shape.radius('lg'),
      p: isMobile ? 2 : 3,
      maxHeight: '85dvh',
    }
  }

  return (
    <Dialog
      ref={dialogRef}
      open={open}
      keepMounted
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return
        onClose?.(event, reason)
      }}
      PaperProps={{
        sx: (theme) =>
          isMobile
          ? getPaperStyle(theme)
          : {
              maxWidth: width,
              width: '100%',
              borderRadius: 4,
              p: isMobile ? 2 : 3,
              maxHeight: '85dvh',
            },
          }}
    >
      {/* 제목 */}
      {title && (
        <DialogTitle
          component="div"
          sx={{
            mb: 2,
            p: 0,
            pr: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant={isMobile ? 'subtitle2' : 'subtitle1'}
            fontWeight={600}
          >
            {title}
          </Typography>

          {!hideCloseButton && (
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                position: 'absolute',
                right: isMobile ? '16px' : '24px',
                top: isMobile ? '16px' : '24px',
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
      )}

      
      <Divider sx={{ borderColor: 'primary.main', mb: 3 }} />

      {/* 내용 */}
      <DialogContent
        sx={{
          p: 0,
          flex: '1 1 auto',
          overflow: 'auto',
          minHeight: 0,
        }}
      >
        {children}
      </DialogContent>

      {/* 하단 버튼 영역 */}
      <DialogActions
        sx={{
          position: isMobile ? 'sticky' : 'static',
          bottom: 0,
          bgcolor: 'white',
          pb: 0,
          pt: 3,
          gap: 0,
          '& .MuiButton-root': {
            width: isMobile ? '100%' : 'auto',
          },
        }}
      >
        {actions ? (
          actions
        ) : (
          hideCloseButton && (
            <Button
              size="medium"
              variant="outlined"
              color="primary"
              onClick={onClose}
              data-focus-first
            >
              닫기
            </Button>
          )
        )}
      </DialogActions>
    </Dialog>
  )
}
